import { isPlatformBrowser } from '@angular/common';
import { computed, inject, Injectable, PLATFORM_ID, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { GsStructBlock, GsStructTarget } from '../models';
import { PageComponentsHelper } from './components-helpers/page.components.service';
import { DateService } from './date/date.service';
import { EnvironmentService } from './environment/environment.service';
import { UrlResolverService } from './url/url-resolver.service';

const TARGET_MAP = {
  0: '_blank',
  1: '_self'
};

@Injectable({
  providedIn: 'root'
})
export class SystemService {
  // Services
  protected readonly envService = inject(EnvironmentService);
  private readonly dateService = inject(DateService);
  private readonly pageComponentsHelper = inject(PageComponentsHelper);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly urlResolver = inject(UrlResolverService);

  // Properties & Signals
  /** Observable for when the user clicks */
  userClickedEvent?: Observable<void>;
  /** Override the development mode (undefined = no override) */
  overrideDevelopmentMode?: boolean;
  /** Allow pointer events on designer components (activated via secret search phrase) */
  devPointerEventsEnabled: boolean = false;
  /** List of active modules */
  activeModules: string[] = [];
  /** Is the webshop module active? */
  isWebshop: boolean = false;
  /** Does the system have a watchlist? */
  hasWatchlist: boolean = false;
  /** Is the sidebar currently opened? Change it through {@link toggleSidebar} */
  readonly sidebarOpen: Signal<boolean> = computed(() => this.isSidebarOpen());
  /** Current relative link without query params */
  currentRelativeLink: Signal<string> = computed<string>(() => {
    let url = this.router.currentNavigation()?.finalUrl?.toString() || this.router.url;
    return url.split('?')[0];
  });
  /** Is the browser detected? False  if server-side is detected */
  private readonly isBrowser: boolean = false;
  /** Writable source of {@link sidebarOpen} */
  private readonly isSidebarOpen: WritableSignal<boolean> = signal(false);
  /** Currently opened Sidebar Menu */
  private activeMenu = 'main';

  // Getters & Setters
  /** Is this currently the browser? (used for non server-side operations) */
  get browser(): boolean {
    return this.isBrowser;
  }
  /** Should the current page be rendered as a full Page? */
  get fullPage(): boolean {
    return this.router.url == '/config';
  }
  /** Is the app running in dev-mode? */
  get isDevelopment(): boolean {
    return this.overrideDevelopmentMode ?? this.envService.env.ENVIRONMENT === 'development';
  }
  /** Sidebar ActiveMenu */
  get activeMenuState(): string {
    return this.activeMenu;
  }

  // Methods
  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.userClickedEvent = new Observable<void>((observer) => {
      document.addEventListener('click', () => observer.next());
    });

    /** Webshop functions (cart, watchlist, orders, guest registration) are enabled by the 'webshop' module or the 'voucher' module, since selling vouchers needs the full shop flow. */
    this.isWebshop = this.envService.hasAccessToModules(['*', 'webshop', 'voucher']);
    this.activeModules = this.envService.modules;
  }

  /**
   * Toggle the state of the sidebar
   * @param state The state to set the sidebar to
   */
  toggleSidebar(state?: 'open' | 'closed'): void {
    this.isSidebarOpen.update((open) => (state ? state === 'open' : !open));
  }

  /**
   * Sets the active menu to be displayed.
   *
   * This method is used to change the active menu within the sidebar.
   * Based on the provided parameter, the active menu is set and the corresponding
   * menu is displayed.
   *
   * @param menu - The name of the menu to be set as active.
   *               This can be any string corresponding to one of the available menus,
   *               e.g., 'main', 'sortiment', 'about', 'contact'.
   */
  setActiveMenu(menu: string): void {
    this.activeMenu = menu;
  }

  /**
   * Generates a navigation link based on the provided URL. Relative paths resolve against the
   * frontend app origin; for backend files/media use {@link UrlResolverService} ('backend'/'media').
   * @param link The link to open
   */
  getLink(link?: string): string | undefined {
    return this.urlResolver.resolveUrl(link, 'frontend');
  }

  /**
   * Opens a provided link either in the same tab (_self) or a new tab/window (_blank).
   * @param link The link to open
   * @param target The target window for the link.
   */
  openLink(link?: string, target: GsStructTarget = GsStructTarget._self): void {
    const fullURL = this.getLink(link);
    window.open(fullURL, TARGET_MAP[target]);
  }

  /**
   * Reloads the current page. Wraps `window.location.reload()` so callers depend on this abstraction
   * instead of the global `location` (which is non-configurable under jsdom and thus not stubbable).
   */
  reloadPage(): void {
    window.location.reload();
  }

  /**
   * Log a current state but only on the browser-side by default
   * @param text A informative text to the logged object
   * @param obj The object to log
   * @param server If the log should be shown on the server-side
   */
  log(text: string, obj?: any, server?: boolean) {
    if ((this.isBrowser && !server) || (!this.isBrowser && server)) {
      console.debug(text, JSON.parse(JSON.stringify(obj ?? 'undefined/null')));
    }
  }

  /**
   * Log a current state
   * @param text A informative text to the logged object
   * @param obj The object to log
   */
  static debug(text: string, obj?: any) {
    console.debug(text, JSON.parse(JSON.stringify(obj ?? 'undefined/null')));
  }

  /**
   * Filter unwanted blocks (without components or with wrong components or inactive)
   * @param blocks - The array of `GsStructBlock` objects to be filtered.
   * @param useIsActive - A boolean flag indicating whether to filter based on the component's active status(activeFrom, activeUntil).
   * @param skipErrors - A boolean flag indicating whether to skip components with errors.
   * @param idPrefix - Prefix for deterministic client ids (see `addClientId`); random ids without it.
   * @returns An array of `GsStructBlock` objects that meet the filtering criteria.
   */
  filterBlocks(blocks: GsStructBlock[], useIsActive = true, skipErrors = false, idPrefix?: string): GsStructBlock[] {
    blocks = blocks.filter((block) => {
      const components = block.components;
      if (!(components && components.length > 0)) return true;
      block.components = components.filter((component) => {
        const isValidComponentType = this.pageComponentsHelper.getComponentTypes().includes(component.componentType);
        const isActive = this.dateService.isBetween(component.activeFrom, component.activeUntil);
        const hasError = component.error && !component.render;
        if (hasError) {
          this.log(`Component with Client-ID ${component.clientId} has an error`, component.error);
        }
        return isValidComponentType && (skipErrors ? !hasError : true) && (!useIsActive || isActive);
      });
      return block.components.length > 0;
    });

    this.addClientId(blocks, idPrefix);
    return blocks;
  }

  /**
   * Sets the client ids of the blocks and their components.
   * With a prefix the ids derive from the block/component index, so the server render and the hydrated
   * browser render produce the same `id`/`aria-*` attributes. Without one (Designer) they are random, so
   * ids of a replaced block list never match references to the previous one.
   * @param blocks - The blocks to set the client ids on.
   * @param idPrefix - Prefix unique per rendered page, e.g. `page-<id>`.
   */
  addClientId(blocks: GsStructBlock[], idPrefix?: string): void {
    const randomId = () => Math.floor(Math.random() * 100000000).toString();
    blocks.forEach((block, blockIndex) => {
      block.clientId = idPrefix ? `${idPrefix}-${blockIndex}` : randomId();
      block.components?.forEach((component, componentIndex) => {
        component.clientId = idPrefix ? `${idPrefix}-${blockIndex}-${componentIndex}` : randomId();
      });
    });
  }

  /**
   * Sets the development mode override.
   * @param overrideWith - A boolean value to override the development mode (undefined = no override).
   */
  setDevelopmentMode(overrideWith: boolean | undefined): void {
    this.overrideDevelopmentMode = overrideWith;
  }

  /**
   * Determines if the current device is a mobile device.
   * Uses the user agent string to detect common mobile platforms.
   * @returns {boolean} True if the device is mobile (Android, iPhone, iPad, iPod), otherwise false.
   */
  isMobile(): boolean {
    if (!this.isBrowser) return false;
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  /**
   * Detects search-engine crawlers and rendering tools via the user agent.
   * Covers the tokens vanilla-cookieconsent's own `hideFromBots` matches plus the
   * Google tools that lack a `bot`/`crawl` token — notably `Google-InspectionTool`
   * (Search Console URL inspection) — so the cookie banner is never part of the
   * rendered page a crawler sees.
   * @returns {boolean} True if the current user agent is a known crawler/inspection tool.
   */
  isCrawler(): boolean {
    if (!this.isBrowser) return false;
    return (
      /bot|crawl|spider|slurp|teoma|google-inspectiontool|chrome-lighthouse|mediapartners|adsbot/i.test(navigator.userAgent) ||
      navigator.webdriver
    );
  }
}
